import { useEffect } from 'react';

import { ThemeStyles, useThemeContext } from '@/contexts/ThemeContext';
import MetaBlock from '@/components/meta-block/MetaBlock';

export default function PrivacyPolicy() {
    const theme = useThemeContext();
    const canonicalUrl = process.env.SITE_URL || 'https://www.roomstay.io' + '/privacy';

    useEffect(() => {
        theme.setThemeStyle(ThemeStyles.Light);
    }, [theme]);

    return (
        <>
            <MetaBlock
                title="Privacy Policy Statement | RoomStay"
                description="Learn how RoomStay collects, uses & protects your data. Read our full privacy policy to understand your rights & our commitment to data security."
            >
                <link rel="canonical" href={canonicalUrl} />
            </MetaBlock>
            <div className="centered-body">
                <div className="container --small">
                    <div className="u-marg-bottom--largest">
                        <h1 className="headline --primary-gradient">Privacy Policy</h1>
                        <p className="body-text --large">Updated May 24, 2025.</p>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">1. Background</h2>
                        <p className="body-text ">
                            Thanks for visiting our Privacy Policy <strong>(Policy)</strong>, we are Roomstay Pty Ltd ACN 641 449 201 of Suite 701 Level 7, 88 Foveaux Street, Surry
                            Hills NSW 2010 <strong>(Roomstay, we, our, us and other similar terms)</strong>. We are committed to providing quality services to you and this policy
                            outlines our ongoing obligations in respect of how we manage your Personal Information. It applies to our software Terms of Use and other agreements we
                            may form with you. When we talk about Personal Information, we mean information or an opinion about an identifiable individual (not a company), whether
                            or not that information or opinion is true or in a material form <strong>(Personal Information)</strong>.
                        </p>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">2. Disclaimer</h2>
                        <p className="body-text ">
                            While your Privacy is important to us, nothing in this Privacy Policy constitutes a voluntary opt in to any privacy laws, anywhere in the world, which
                            we are not statutorily bound to comply with.
                        </p>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">3. Collection of Personal Information</h2>
                        <div className="u-pad--large u-pad-top--none">
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">3.1. How we collect Personal Information</h3>
                                <p className="body-text">
                                    We collect Personal Information in the ordinary course of our business, which is the provision of a booking engine <strong>(Platform)</strong>.
                                    Personal Information is collected when you:
                                </p>
                                <ul className="lettered-list">
                                    <li>Contact or correspond with us via email, telephone or via our website;</li>
                                    <li>Make a booking on our Platform;</li>
                                    <li>Create an account on our Platform;</li>
                                    <li>Subscribe to our newsletters or complete surveys and online forms;</li>
                                    <li>Purchase or enquire about our Platform;</li>
                                    <li>Seek support in relation to our Platform; or</li>
                                    <li>Make customer service related enquiries.</li>
                                </ul>

                                <p className="body-text">Information will only be collected directly from you unless you authorise another person to provide the information.</p>
                            </div>
                            <div className="u-marg-bottom">
                                <h3 className="headline">3.2. What Personal Information is collected?</h3>
                                <p className="body-text">
                                    The types of Personal Information we collect includes your name, address, telephone number, email, credit card details, IP address, device
                                    identification, and any additional information you provide to us.
                                    <br />
                                    <br />
                                    Where you contact us on behalf of your employer, the information you provide often contains information about your employment, position and
                                    employers contact details. In those circumstances certain employment information is collected.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">4. How Roomstay uses Personal Information</h2>
                        <div className="u-pad--large u-pad-top--none">
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">4.1. Why we collect Personal Information</h3>
                                <p className="body-text">
                                    We collect your Personal Information for the primary purpose of providing our Platform to you. We may also use your Personal Information for
                                    secondary purposes closely related to the primary purpose, in circumstances where you would reasonably expect such use or disclosure.
                                    <br />
                                    <br />
                                    Examples of when we may use your Personal Information include:
                                </p>
                                <ul className="lettered-list">
                                    <li>Informing you about our Platform;</li>
                                    <li>Providing you with access to the Platform;</li>
                                    <li>Administration needs in relation to providing you with our Platform, including the administration of your account;</li>
                                    <li>Dealing with requests, enquiries or complaints and other customer care related activities; and</li>
                                    <li>
                                        Carrying out any activity in connection with a legal, governmental or regulatory requirement imposed on us or in connection with legal
                                        proceedings, crime or fraud prevention, detection or prosecution.
                                    </li>
                                </ul>
                            </div>
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">4.2. Direct marketing</h3>
                                <p className="body-text">
                                    Where you provide us with consent to do so (e.g. if you have subscribed to our email lists or have indicated that you are interested in
                                    receiving offers or information from us), we send you marketing communications by email about products and services that we feel may be of
                                    interest to you.
                                    <br />
                                    <br />
                                    You can opt-out of such communications if you would prefer not to receive them in the future by using the “unsubscribe” facility provided in the
                                    communication itself.
                                </p>
                            </div>
                            <div className="u-marg-bottom">
                                <h3 className="headline">4.3. Use of de-identified data</h3>
                                <p className="body-text">
                                    The data we collect may have analytical value to us, our business partners and our related entities. We reserve the right to process, distribute
                                    or sell information we collect through our services. However, we will only distribute data which has been de-identified or otherwise to our
                                    associates in accordance with this Policy.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">5. Cookies and browser analytics</h2>
                        <div className="u-pad--large u-pad-top--none">
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">5.1. Cookies, pixels and logs</h3>
                                <p className="body-text">
                                    When you visit our website or engage with us through social media, certain information is collected. This is generally anonymous information and
                                    it does not reveal your identity. However, if you are logged into your account some of this information could be associated with you.
                                </p>
                                <ul className="lettered-list">
                                    <li>Your IP address or proxy server IP address;</li>
                                    <li>The website which referred you to ours;</li>
                                    <li>The domain name and web pages you access;</li>
                                    <li>The date, time and duration of your visit to the website;</li>
                                    <li>The number of times you access our site within any month;</li>
                                    <li>The operating system of your computer or mobile device; and</li>
                                    <li>The technical capabilities of your web browser.</li>
                                </ul>
                                <p className="body-text">
                                    By modifying your browser preferences, you have the choice to accept all cookies, to be notified when a cookie is set, or to reject all cookies.
                                    If you choose to reject cookies some parts of our website may not work properly.
                                </p>
                            </div>
                            <div className="u-marg-bottom--large">
                                <h3 className="headline">5.2. Use of Google Analytics</h3>
                                <p className="body-text">
                                    This website uses Google Analytics, a web analytics service provided by Google LLC (Google). Google analytics uses cookies, which are text files
                                    placed on your computer, to analyse how you use the site.
                                    <br />
                                    <br />
                                    The information collected by Google about your use of the website will be transmitted to and stored by Google on servers in the United States.
                                    Google will use this information for the purpose of evaluating your use of the website, compiling reports on website activity for website
                                    operators and providing other services relating to website activity and internet usage.
                                    <br />
                                    <br />
                                    Google may also transfer this information to third parties where required to do so by law, or where such third parties process the information
                                    on Google&apos;s behalf.
                                    <br />
                                    <br />
                                    By using this website, you consent to the processing of data about you by Google in the manner described in Google&apos;s privacy policy and for
                                    the purposes set out above. You can opt out of Google Analytics if you disable or refuse the cookie, disable JavaScript, or use the opt-out
                                    service provided by Google.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">6. Data retention and security</h2>
                        <div className="u-pad--large u-pad-top--none">
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">6.1. Security mechanisms we employ</h3>
                                <p className="body-text">
                                    Generally, we store your Personal Information using secure servers protected from unauthorised access, modification and disclosure. However,
                                    like most businesses we hold some information on our staff&apos;s computers (such as emails from you) and where necessary as hard copy files
                                    (such as printed invoices).
                                    <br />
                                    <br />
                                    Our systems are located in Australia and are managed by us and our service providers. Personal Information that we store or transmit is
                                    protected by security and access controls, including username and password authentication, two-factor authentication, and data encryption (such
                                    as SSL) where appropriate.
                                    <br />
                                    <br />
                                    In our dealings with third party service providers, we take care to work with subcontractors and service providers who we believe maintain an
                                    acceptable standard of data security compliance.
                                </p>
                            </div>
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">6.2. How long we keep your Personal Information</h3>
                                <p className="body-text">
                                    We retain your Personal Information for as long as is necessary to provide our Platform to you, as required for our internal business
                                    operations, and to comply with our legal obligations
                                    <br />
                                    <br />
                                    If we hold Personal Information about you, and we do not need that information for any purpose, we will take reasonable steps to destroy or
                                    de-identify that information, in accordance with the Australian Privacy Principles <strong>(APP)</strong> and the European Union General Data
                                    Protection Regulation <strong>(GDPR)</strong>, unless we are prevented from doing so by law.
                                    <br />
                                    <br />
                                    Under Australian law, financial records, such as those relating to financial transactions, must be retained for 7 years after the transactions
                                    associated with those records are completed.
                                    <br />
                                    <br />
                                    If you no longer want us to use your Personal Information, you can request that we erase it and close your account. Where possible we will do so
                                    in accordance with the APPs and GDPR. However, where you request the erasure of your Personal Information we will retain information from
                                    deleted accounts as necessary for our legitimate business interests, to comply with the law, prevent fraud, collect fees, resolve disputes,
                                    troubleshoot problems, assist with investigations or requests by government, a court of law, or law enforcement authorities, enforce the terms
                                    of service and take other actions permitted by law. Any information we retain will be handled in accordance with this privacy policy.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">7. Disclosure of your Personal Information</h2>
                        <div className="u-pad--large u-pad-top--none">
                            <div className="u-marg-bottom--largest">
                                <h3 className="headline">7.1. Who we share your Personal Information with?</h3>
                                <p className="body-text">Your Personal Information may be disclosed to:</p>
                                <ul className="lettered-list">
                                    <li>Our employees, a related company and our professional advisers (lawyers, accountants, financial advisers etc.);</li>
                                    <li>Where you have made a booking on our Platform, to the accommodation provider of your booking;</li>
                                    <li>
                                        Regulators and government authorities in connection with our compliance procedures and obligations, including law enforcement agencies to
                                        assist in the investigation and prevention of criminal activities;
                                    </li>
                                    <li>A third party, in order to enforce or defend our rights, or to address financial or reputational risks;</li>
                                    <li>Third party contractors, suppliers and service providers with whom we have a business association, including:</li>
                                    <ul className="roman-list">
                                        <li>Delivery and transportation providers;</li>
                                        <li>Payment gateway providers, such as Very Good Security Inc.;</li>
                                        <li>Third party property management systems we have an API integration with, such as Sabre Corporation the owner of SynXis;</li>
                                        <li>Administration service providers;</li>
                                        <li>Marketing service providers; and </li>
                                        <li>Information technology service providers including cloud application providers.</li>
                                    </ul>
                                </ul>
                                <p className="body-text">We will not disclose your Personal Information other than in accordance with this Policy without your consent.</p>
                            </div>
                            <div className="u-marg-bottom--large">
                                <h3 className="headline">7.2. Offshore transfers</h3>
                                <p className="body-text">
                                    We may disclose your Personal Information to third party contractors, service providers and suppliers with whom we have a business association
                                    who operate in Australia and United States of America.
                                    <br />
                                    <br />
                                    While we do not otherwise actively disclose your Personal Information to overseas entities, our engagement of service providers, such as those
                                    who operate cloud services, may have international data centres and disaster recovery sites. Consequently, these providers may have access to
                                    your information offshore. We rely solely on reputable organisations for such cloud services.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">8. Anonymity and use of Pseudonyms</h2>
                        <p className="body-text">
                            If you contact us with a general enquiry, we may interact with you anonymously or through the use of pseudonyms. However, you are required to provide
                            true and accurate details when requesting the supply of goods or provision of services. You agree you will provide accurate information if we require
                            it.
                        </p>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">9. Access to Personal Information and corrections</h2>
                        <p className="body-text">
                            We endeavour to only hold Personal Information that is accurate, complete and up-to-date. You have the right to make a request to access Personal
                            Information which we hold about you and to request corrections of any errors in that data. To make an access or correction request, contact us using the
                            contact details provided at the end of this Policy.
                            <br />
                            <br />
                            If you have an account with us, you can access some of the Personal Information that we collect about you. By logging into your account, you can update
                            or correct certain information.
                            <br />
                            <br />
                            In order to protect your Personal Information, when you contact us, we may require identification from you before releasing the requested information or
                            making the correction.
                        </p>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">10. Additional rights for EU residents and citizens</h2>
                        <p className="body-text">
                            For the purposes of the GDPR, we are largely a &apos;data processor&apos; of Personal Information. If you&apos;re a citizen or resident of the European
                            Economic Area, the following rights apply to you.
                            <br />
                            <br />
                            You are entitled to ask us to port your Personal Information (i.e. to transfer in a structured, commonly used and machine-readable format, to you), to
                            erase it, or restrict its processing. You also have rights to object to some processing that is based on our legitimate interests, such as profiling
                            that we perform for the purposes of direct marketing, and, where we have asked for your consent to process your data, to withdraw this consent.
                            <br />
                            <br />
                            These rights are limited in some situations - for example, where we can demonstrate that we have a legal requirement to process your Personal
                            Information. In some instances, this means that we may retain some data even if you withdraw your consent.
                            <br />
                            <br />
                            Where we require your Personal Information to comply with legal or contractual obligations, then provision of such data is mandatory and if you do not
                            provide it then we will not be able to manage our contractual relationship with you, or to meet obligations placed on us. In those cases, you must
                            provide us with your Personal Information, otherwise the provision of requested Personal Information is optional.
                            <br />
                            <br />
                            If you have unresolved concerns, you also have the right to complain to data protection authorities. The relevant data protection authority will be the
                            data protection authority in the country:
                        </p>
                        <ul className="lettered-list">
                            <li>Of your habitual residence; </li>
                            <li>Of your place of work; or </li>
                            <li>In which you consider the alleged infringement has occurred.</li>
                        </ul>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">11. Complaints</h2>
                        <p className="body-text">
                            Your privacy is important to us. If you have a complaint or concern about our handling of your Personal Information, we ask that you first contact our
                            privacy officer whose contact details are listed below. We will investigate your complaint and reply to you in writing if you provide us with contact
                            details and request us to do so.
                            <br />
                            <br />
                            If, after we have conducted our investigations you are still not satisfied then we ask you to consult with the Office of the Australian Information
                            Commissioner:
                        </p>
                        <dl>
                            <dt>Email:</dt>
                            <dd>
                                <a href="mailto:enquiries@oaic.gov.au" className="link --secondary">
                                    enquiries@oaic.gov.au
                                </a>
                            </dd>
                            <dt>Telephone:</dt>
                            <dd>
                                <a href="tel:1300 363 992" className="link --secondary">
                                    1300 363 992
                                </a>{' '}
                                (from overseas{' '}
                                <a href="tel:+61 2 9284 9749" className="link --secondary">
                                    +61 2 9284 9749
                                </a>
                                )
                            </dd>
                            <dt>Post:</dt>
                            <dd>
                                GPO Box 5218
                                <br />
                                Sydney NSW 2001
                            </dd>
                        </dl>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">12. Contacting Roomstay</h2>
                        <p className="body-text">
                            If you have any questions about our privacy practices, wish to make a complaint or have questions regarding the way in which we have been managing your
                            Personal Information, please contact our privacy officer:
                        </p>
                        <dl>
                            <dt>Email:</dt>
                            <dd>
                                <a href="mailto:hello@roomstay.io " className="link --secondary">
                                    hello@roomstay.io{' '}
                                </a>
                            </dd>
                            <dt>Telephone:</dt>
                            <dd>
                                <a href="tel:(02) 8091 7884" className="link --secondary">
                                    (02) 8091 7884
                                </a>
                            </dd>
                        </dl>
                    </div>
                    <div className="u-marg-bottom--largest">
                        <h2 className="headline --smaller --with-spacer">13. Variations to this Policy</h2>
                        <p className="body-text">
                            We will need to change this policy from time to time in order to make sure it stays up to date with the latest legal requirements and any changes to our
                            privacy management practices.
                            <br />
                            <br />
                            When we do change the policy, we&apos;ll make sure to notify you about such changes, where required. A copy of the latest version of this policy will
                            always be available on this page.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
